import { useState } from 'react';

import { PlusIcon } from 'lucide-react';
import {
  UseFormReturn,
  FormProvider,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';

import { FallbackNoResponseCardHeader } from './FallbackNoResponseCardHeader';
import { IntegrationSelectContent } from './IntegrationSelectContent';
import { MessageAuthoring } from './MessageAuthoring';
import { MessagePreview } from './MessagePreview';
import { useCampaignBuildLogic } from './useCampaignsBuildForm';
import {
  CampaignForm,
  DEFAULT_NO_RESPONSE_WAIT_MINUTES,
  EMPTY_SENDER,
  isAmdEnabledIntegration,
} from '../../utils';

interface CampaignBuildFormProps {
  methods: UseFormReturn<CampaignForm>;
}

export function CampaignBuildForm({ methods }: CampaignBuildFormProps) {
  const { t } = useTranslation();
  const { setValue, control } = methods;
  const formState = useFormState({ control });
  // State to track which sender is currently active for preview
  // 'primary' = primary sender, 'not_delivered_fallback' = fallback if not delivered (index 0), number = fallback if no response (index 1+)
  const [activeSender, setActiveSender] = useState<'primary' | 'not_delivered_fallback' | number>(
    'primary'
  );

  // useFieldArray for managing dynamic fallback senders
  const { fields, append, remove } = useFieldArray({ control, name: 'fallbackSenders' });

  /**
   * Handles deletion of a fallback sender from the "no response" section
   */
  const handleDeleteFallbackSender = (senderIndex: number) => {
    remove(senderIndex);
    // Reset active sender if the deleted one was active
    if (activeSender === senderIndex) {
      setActiveSender('primary');
    } else if (typeof activeSender === 'number' && activeSender > senderIndex) {
      // Adjust active sender index if it was after the deleted one
      setActiveSender(activeSender - 1);
    }
  };

  // Extract all business logic to custom hook
  const {
    whatsappTemplatesMap,
    getTemplatesForIntegration,
    loadingIntegrations,
    loadingTemplates,
    primarySender,
    fallbackSenders,
    fallbackIfNotDelivered,
    allIntegrations,
    onPrimarySenderChange,
    onFallbackSenderChange,
    onNoResponseSenderChange,
  } = useCampaignBuildLogic(methods);
  const fallbackIfNoResponse = useWatch({ control, name: 'fallbackIfNoResponse' });

  // Fallback toggles only appear once the first (primary) channel is completed:
  // a channel is selected and its message is authored (WhatsApp needs a template,
  // other channels need free-text content).
  const isPrimaryChannelComplete = Boolean(
    primarySender?.integrationId &&
    primarySender?.integrationType &&
    (primarySender.integrationType === 'whatsapp'
      ? primarySender.template?.id?.trim()
      : primarySender.content?.trim())
  );

  // AMD gate for the voicemail action toggle
  const isSenderAmdEnabled = (integrationId?: string) =>
    isAmdEnabledIntegration(allIntegrations, integrationId);

  // Check for variable-related errors (headerParameters, bodyParameters, footerParameters)
  const getVariableErrorMessage = (senderPath?: string) => {
    const { errors } = formState;

    if (senderPath) {
      // Check for specific sender path errors (e.g., "fallbackSenders.0.headerParameters")
      const senderVariableFields = [
        `${senderPath}.headerParameters`,
        `${senderPath}.bodyParameters`,
        `${senderPath}.footerParameters`,
        `${senderPath}.headerMediaParam.type`,
        `${senderPath}.headerMediaParam.value`,
      ];

      // Check if any variable field has errors for this specific sender
      const senderVariableErrors = senderVariableFields
        .map((field) => {
          const fieldParts = field.split('.');
          let errorObj: unknown = errors;
          for (const part of fieldParts) {
            if (errorObj && typeof errorObj === 'object' && part in errorObj) {
              errorObj = (errorObj as Record<string, unknown>)[part];
            } else {
              return null;
            }
          }
          return errorObj;
        })
        .filter(Boolean);

      if (senderVariableErrors.length > 0) {
        // Check for specific error types
        for (const error of senderVariableErrors) {
          if (error && typeof error === 'object' && 'message' in error) {
            // If it's a validation error with a specific message, return that
            if (typeof error.message === 'string') {
              return error.message;
            }
          }
        }
        // Fallback message for required variables
        return t('campaigns.build.all_variables_required');
      }
    } else {
      // Original logic for primary sender (backward compatibility)
      const variableFields = ['headerParameters', 'bodyParameters', 'footerParameters'];

      // Check if any variable field has errors
      const variableErrors = variableFields
        .map((field) => errors[field as keyof typeof errors])
        .filter(Boolean);

      if (variableErrors.length === 0) {
        return null;
      }

      // Check for specific error types
      for (const error of variableErrors) {
        if (error && typeof error === 'object' && 'message' in error) {
          // If it's a validation error with a specific message, return that
          if (typeof error.message === 'string') {
            return error.message;
          }
        }
      }

      // Fallback message for required variables
      return t('campaigns.build.all_variables_required');
    }

    return null;
  };

  return (
    <FormProvider {...methods}>
      <Form {...methods}>
        <div className="flex w-full max-w-5xl flex-col gap-4 rounded-xl bg-muted p-1 lg:flex-row">
          {/* Left Panel - Scrollable Form */}
          <div className="w-full pb-20 lg:w-[500px] lg:overflow-y-auto lg:pr-2">
            <form onSubmit={(e) => e.preventDefault()}>
              {/* Primary Sender Configuration Section */}
              <Card className="mb-8 bg-background pb-7 text-foreground shadow-none">
                <CardContent className="space-y-6">
                  <FormField
                    control={methods.control}
                    name="primarySender.integrationId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">
                          {t('campaigns.build.send_from')}
                        </FormLabel>
                        <IntegrationSelectContent
                          integrations={allIntegrations}
                          loading={loadingIntegrations}
                          value={field.value || ''}
                          onValueChange={(value) => {
                            field.onChange(value);
                            onPrimarySenderChange(value);
                          }}
                          onOpenChange={(open) => {
                            if (open) {
                              setActiveSender('primary');
                            }
                          }}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Message authoring based on integration type */}
                  {primarySender?.integrationType && (
                    <div
                      onClick={() => setActiveSender('primary')}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                          e.preventDefault();
                          setActiveSender('primary');
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={t('campaigns.build.primary_sender')}
                    >
                      <MessageAuthoring
                        integrationType={primarySender.integrationType}
                        content={primarySender.content}
                        templateId={primarySender?.template?.id}
                        templates={getTemplatesForIntegration(primarySender.integrationId)}
                        loadingTemplates={loadingTemplates}
                        onContentChange={(content) => setValue('primarySender.content', content)}
                        onTemplateChange={(template) =>
                          setValue('primarySender.template', template)
                        }
                        variableErrorMessage={getVariableErrorMessage('primarySender')}
                        senderPath="primarySender"
                        onFocus={() => setActiveSender('primary')}
                        amdEnabled={isSenderAmdEnabled(primarySender.integrationId)}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Fallback options appear only after the first channel is completed */}
              {isPrimaryChannelComplete && (
                <>
                  {/* Fallback Settings */}
                  <FormField
                    control={methods.control}
                    name="fallbackIfNotDelivered"
                    render={({ field }) => (
                      <FormItem className="mb-3 flex items-center justify-between space-y-0 px-4">
                        <span className="text-sm font-medium">
                          {t('campaigns.build.fallback_settings')}
                        </span>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={(value) => {
                              if (!value) {
                                setActiveSender('primary');
                              }
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Fallback Sender Configuration */}
                  {fallbackIfNotDelivered && (
                    <Card className="mb-8 bg-background pb-7 text-foreground shadow-none">
                      <CardContent className="space-y-6">
                        <FormField
                          control={methods.control}
                          name="fallbackSenders.0.integrationId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium">
                                {t('campaigns.build.send_from')}
                              </FormLabel>
                              <IntegrationSelectContent
                                integrations={allIntegrations}
                                loading={loadingIntegrations}
                                value={field.value || ''}
                                onValueChange={(value) => {
                                  field.onChange(value);
                                  onFallbackSenderChange(value);
                                }}
                                onOpenChange={(open) => {
                                  if (open) {
                                    setActiveSender('not_delivered_fallback');
                                  }
                                }}
                              />
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Fallback message authoring */}
                        {fallbackSenders?.[0]?.integrationType && (
                          <div
                            onClick={() => setActiveSender('not_delivered_fallback')}
                            onKeyDown={(e) => {
                              if (
                                (e.key === 'Enter' || e.key === ' ') &&
                                e.target === e.currentTarget
                              ) {
                                e.preventDefault();
                                setActiveSender('not_delivered_fallback');
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={t('campaigns.build.fallback_sender')}
                          >
                            <MessageAuthoring
                              integrationType={fallbackSenders?.[0].integrationType}
                              content={fallbackSenders?.[0].content}
                              templateId={fallbackSenders?.[0]?.template?.id}
                              templates={getTemplatesForIntegration(
                                fallbackSenders?.[0]?.integrationId
                              )}
                              loadingTemplates={loadingTemplates}
                              onContentChange={(content) =>
                                setValue('fallbackSenders.0.content', content)
                              }
                              onTemplateChange={(template) =>
                                setValue('fallbackSenders.0.template', template)
                              }
                              variableErrorMessage={getVariableErrorMessage('fallbackSenders.0')}
                              senderPath="fallbackSenders.0"
                              onFocus={() => setActiveSender('not_delivered_fallback')}
                              amdEnabled={isSenderAmdEnabled(fallbackSenders?.[0]?.integrationId)}
                            />
                          </div>
                        )}

                        {/* Display validation errors for fallback senders array */}
                        <FormField
                          control={methods.control}
                          name="fallbackSenders"
                          render={() => (
                            <FormItem>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>
                  )}

                  {/* Follow-up Fallback Settings (No Response) */}
                  <FormField
                    control={methods.control}
                    name="fallbackIfNoResponse"
                    render={({ field }) => (
                      <FormItem className="mb-3 flex items-center justify-between space-y-0 px-4">
                        <span className="text-sm font-medium">
                          {t('campaigns.build.followup_fallback_settings')}
                        </span>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={(value) => {
                              if (!value) {
                                setActiveSender('primary');
                              }
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Follow-up Sender Configuration (No Response) - Dynamic array starting from index 1 */}
                  {fallbackIfNoResponse && (
                    <div className="space-y-3">
                      {fields.slice(1).map((field, arrayIndex) => {
                        // arrayIndex is 0-based for the slice, so actual index = arrayIndex + 1
                        const senderIndex = arrayIndex + 1;
                        const senderPath = `fallbackSenders.${senderIndex}` as const;

                        return (
                          <Card
                            key={field.id}
                            className="bg-background pb-7 text-foreground shadow-none"
                          >
                            <FallbackNoResponseCardHeader
                              control={methods.control}
                              senderIndex={senderIndex}
                              showDeleteButton={senderIndex > 1}
                              onDelete={() => handleDeleteFallbackSender(senderIndex)}
                            />
                            <CardContent className="space-y-6">
                              <FormField
                                control={methods.control}
                                name={`fallbackSenders.${senderIndex}.integrationId`}
                                render={({ field: integrationField }) => (
                                  <FormItem>
                                    <FormLabel className="text-sm font-medium">
                                      {t('campaigns.build.send_from')}
                                    </FormLabel>
                                    <IntegrationSelectContent
                                      integrations={allIntegrations}
                                      loading={loadingIntegrations}
                                      value={integrationField.value || ''}
                                      onValueChange={(value) => {
                                        integrationField.onChange(value);
                                        onNoResponseSenderChange(value, senderIndex);
                                      }}
                                      onOpenChange={(open) => {
                                        if (open) {
                                          setActiveSender(senderIndex);
                                        }
                                      }}
                                    />
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              {/* Follow-up message authoring */}
                              {fallbackSenders?.[senderIndex]?.integrationType && (
                                <div
                                  onClick={() => setActiveSender(senderIndex)}
                                  onKeyDown={(e) => {
                                    if (
                                      (e.key === 'Enter' || e.key === ' ') &&
                                      e.target === e.currentTarget
                                    ) {
                                      e.preventDefault();
                                      setActiveSender(senderIndex);
                                    }
                                  }}
                                  tabIndex={0}
                                  role="button"
                                >
                                  <MessageAuthoring
                                    integrationType={fallbackSenders?.[senderIndex].integrationType}
                                    content={fallbackSenders?.[senderIndex].content}
                                    templateId={fallbackSenders?.[senderIndex]?.template?.id}
                                    templates={getTemplatesForIntegration(
                                      fallbackSenders?.[senderIndex]?.integrationId
                                    )}
                                    loadingTemplates={loadingTemplates}
                                    onContentChange={(content) =>
                                      setValue(`fallbackSenders.${senderIndex}.content`, content)
                                    }
                                    onTemplateChange={(template) =>
                                      setValue(`fallbackSenders.${senderIndex}.template`, template)
                                    }
                                    variableErrorMessage={getVariableErrorMessage(senderPath)}
                                    senderPath={senderPath}
                                    onFocus={() => setActiveSender(senderIndex)}
                                    amdEnabled={isSenderAmdEnabled(
                                      fallbackSenders?.[senderIndex]?.integrationId
                                    )}
                                  />
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}

                      {/* Add another button */}
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-primary"
                        onClick={() => {
                          append({
                            ...EMPTY_SENDER,
                            waitTimeMinutes: DEFAULT_NO_RESPONSE_WAIT_MINUTES,
                          });
                        }}
                      >
                        <PlusIcon className="mr-2 h-4 w-4 text-primary" />
                        {t('campaigns.build.add_another')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </form>
          </div>

          {/* Right Panel - Fixed Preview */}
          <div className="w-full flex-1 lg:sticky lg:top-0 lg:h-fit lg:w-auto">
            <MessagePreview
              integrations={allIntegrations}
              whatsappTemplatesMap={whatsappTemplatesMap}
              activeSender={activeSender}
              primarySender={primarySender}
              fallbackSender={fallbackSenders?.[0]}
              noResponseSenders={fallbackSenders?.slice(1)}
            />
          </div>
        </div>
      </Form>
    </FormProvider>
  );
}
